import { useState, useEffect } from 'react';
import { notification } from 'antd';
import Papa from 'papaparse';
import Modal from '../../Modal/Modal';
import { t } from '../../../i18n/i18n';
import DebouncedButton from '../../Button/Button';
import useImportCsv from '../../../hooks/useImportCsv';
import {
  createSubskill,
  updateSubskills,
  deleteSubskill,
} from '../../../API/services/screeningService';

const ImportSubskillsModal = ({ showModal, setShowModal = () => {}, onSuccess = () => {} }) => {
  const [loading, setLoading] = useState(false);
  const [importedData, setImportedData] = useState([]);
  const [previewData, setPreviewData] = useState([]);

  // CSV columns expected: ID (ignored for new), Name (ES), Name (EN), Name (PT), Parent ID
  const { importRef, readFile, result, setResult } = useImportCsv([
    'id',
    'nameEs',
    'nameEn',
    'namePt',
    'parentId',
  ]);

  useEffect(() => {
    if (result && result.length > 0) {
      // Skip header row and process data
      const dataWithoutHeader = result.slice(1);
      const processedData = dataWithoutHeader
        .map((row) => ({
          id: row.id || null,
          nameEs: row.nameEs || '',
          nameEn: row.nameEn || '',
          namePt: row.namePt || '',
          parentId: row.parentId || null,
          action: detectAction(row),
        }))
        .filter((item) => item.nameEs || item.action === 'delete'); // Filter out empty rows except deletes

      setImportedData(processedData);
      setPreviewData(processedData.slice(0, 5)); // Show first 5 for preview
      setLoading(false);
    }
  }, [result]);

  const detectAction = (row) => {
    // Check if any column contains 'DELETE'
    const hasDelete = Object.values(row).some(
      (value) => typeof value === 'string' && value.toUpperCase().trim() === 'DELETE'
    );

    if (hasDelete) return 'delete';
    if (row.id) return 'update';
    return 'create';
  };

  const handleFileSelect = () => {
    importRef.current.click();
  };

  const handleImport = async () => {
    if (importedData.length === 0) {
      notification.error({
        message: t('IMPORT_NO_DATA'),
      });
      return;
    }

    setLoading(true);
    let createCount = 0;
    let updateCount = 0;
    let deleteCount = 0;
    let errorCount = 0;

    for (const subskill of importedData) {
      try {
        if (subskill.action === 'delete') {
          // Delete subskill
          if (subskill.id) {
            const response = await deleteSubskill(subskill.id);
            if (response) {
              deleteCount++;
            } else {
              errorCount++;
            }
          } else {
            errorCount++;
          }
        } else if (subskill.action === 'update') {
          // Update existing subskill
          const body = {
            name: subskill.nameEs,
            languages: {
              es: subskill.nameEs,
              en: subskill.nameEn,
              pt: subskill.namePt,
            },
            parentId: subskill.parentId ? subskill.parentId : null,
          };

          const response = await updateSubskills(subskill.id, body, null);
          if (response) {
            updateCount++;
          } else {
            errorCount++;
          }
        } else {
          // Create new subskill
          const body = {
            name: subskill.nameEs,
            languages: {
              es: subskill.nameEs,
              en: subskill.nameEn,
              pt: subskill.namePt,
            },
            parentId: subskill.parentId ? subskill.parentId : null,
          };
          const response = await createSubskill(body, null);
          if (response) {
            createCount++;
          } else {
            errorCount++;
          }
        }
      } catch (error) {
        errorCount++;
      }
    }

    setLoading(false);

    const totalSuccess = createCount + updateCount + deleteCount;

    if (totalSuccess > 0) {
      let message = '';
      if (createCount > 0) message += t('IMPORT_CREATED').replace('{{count}}', createCount) + ' ';
      if (updateCount > 0) message += t('IMPORT_UPDATED').replace('{{count}}', updateCount) + ' ';
      if (deleteCount > 0) message += t('IMPORT_DELETED').replace('{{count}}', deleteCount);

      notification.success({
        message: message.trim(),
      });
      onSuccess();
      handleClose();
    }

    if (errorCount > 0) {
      notification.warning({
        message: t('IMPORT_ERRORS').replace('{{count}}', errorCount),
      });
    }
  };

  const handleClose = () => {
    setImportedData([]);
    setPreviewData([]);
    setResult(null);
    setShowModal(false);
  };

  const downloadTemplate = () => {
    const headers = ['ID', 'Name (ES)', 'Name (EN)', 'Name (PT)', 'Parent ID'];
    const example1 = ['', 'Desarrollo Web', 'Web Development', 'Desenvolvimento Web', ''];
    const example2 = [
      '123',
      'Desarrollo Móvil Actualizado',
      'Updated Mobile Development',
      'Desenvolvimento Móvel Atualizado',
      '45',
    ];
    const example3 = ['456', 'DELETE', '', '', ''];

    // Use Papa.unparse to properly handle CSV generation with proper escaping
    const csvContent = Papa.unparse(
      {
        fields: headers,
        data: [example1, example2, example3],
      },
      {
        quotes: true, // Automatically quote fields that contain commas
        delimiter: ',',
        header: true,
      }
    );

    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', 'subskills_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Modal
      data-testid="import-subskills-modal"
      className="modal-buttons-border d-flex justify-content-end"
      showCloseButton={false}
      show={showModal}
      minWidth={false}
      setShow={(e) => {
        setShowModal(e);
      }}
      onClose={handleClose}
    >
      <div className="px-5 mx-5" style={{ minWidth: '500px' }}>
        <div className="py-5 d-flex flex-column align-items-center">
          <span className="font-weight-600 text-base mb-4">{t('IMPORT_SUBSKILLS_TITLE')}</span>

          <div className="w-100 mb-4">
            <p className="text-sm mb-2">{t('IMPORT_SUBSKILLS_DESCRIPTION')}</p>
            <p className="text-sm mb-2">
              <strong>{t('EXPECTED_COLUMNS')}:</strong> ID, Name (ES), Name (EN), Name (PT), Parent
              ID
            </p>
            <ul className="text-sm mb-3">
              <li>{t('IMPORT_RULE_CREATE')}</li>
              <li>{t('IMPORT_RULE_UPDATE')}</li>
              <li>{t('IMPORT_RULE_DELETE')}</li>
            </ul>

            <button
              type="button"
              className="btn btn-link p-0 text-primary"
              onClick={downloadTemplate}
            >
              <i className="fas fa-download mr-2"></i>
              {t('DOWNLOAD_TEMPLATE')}
            </button>
          </div>

          <div className="w-100 mb-4">
            <input
              ref={importRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={readFile}
              style={{ display: 'none' }}
            />
            <button
              type="button"
              className="ant-btn ant-btn-primary bg-principal rounded-0 d-flex align-items-center justify-content-center"
              onClick={handleFileSelect}
              disabled={loading}
            >
              <i className="fas fa-file-upload mr-2"></i>
              {t('SELECT_FILE')}
            </button>
          </div>

          {previewData.length > 0 && (
            <div className="w-100 mb-4">
              <h6 className="mb-2">{t('PREVIEW')}:</h6>
              <div className="border rounded p-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                <table className="table table-sm mb-0">
                  <thead>
                    <tr>
                      <th>{t('ACTION')}</th>
                      <th>ID</th>
                      <th>{t('NAME')} (ES)</th>
                      <th>{t('NAME')} (EN)</th>
                      <th>{t('NAME')} (PT)</th>
                      <th>Parent ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.map((item, index) => (
                      <tr key={index}>
                        <td>
                          <span
                            className={`badge badge-${
                              item.action === 'create'
                                ? 'success'
                                : item.action === 'update'
                                ? 'warning'
                                : 'danger'
                            }`}
                          >
                            {t(`ACTION_${item.action.toUpperCase()}`)}
                          </span>
                        </td>
                        <td>{item.id || '-'}</td>
                        <td>{item.nameEs}</td>
                        <td>{item.nameEn}</td>
                        <td>{item.namePt}</td>
                        <td>{item.parentId || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {importedData.length > 5 && (
                  <p className="text-sm text-muted mt-2 mb-0">
                    {t('AND_MORE').replace('{{count}}', importedData.length - 5)}
                  </p>
                )}
              </div>
            </div>
          )}

          <div>
            <button
              type="button"
              className="px-5 py-3 modal-buttons-border bg-transparent mt-3 dont-delete-draft-button-border border-red-danger text-red-danger outline-none text-xs font-weight-700 mr-2"
              onClick={handleClose}
              disabled={loading}
            >
              {t('CANCEL')}
            </button>
            <DebouncedButton
              type="button"
              className="px-5 py-3 modal-buttons-border mt-3 border-0 text-white outline-none text-xs font-weight-700 ml-2"
              style={{ backgroundColor: '#449cc8' }}
              onClick={handleImport}
              disabled={loading || importedData.length === 0}
            >
              {loading
                ? t('IMPORTING')
                : `${t('IMPORT')} (${importedData.filter((i) => i.action === 'create').length} ${t(
                    'NEW'
                  )}, ${importedData.filter((i) => i.action === 'update').length} ${t('UPDATE')}, ${
                    importedData.filter((i) => i.action === 'delete').length
                  } ${t('DELETE')})`}
            </DebouncedButton>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ImportSubskillsModal;
